// apiService.ts - Updated with REAL Azure AD invitation API calls + multi-role support
import { User, Company, Function, UserFormData, UserType } from './types';
import { rolesToString } from './RoleSelector';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://gatepass-api-1753632520.azurewebsites.net/api';

interface ADInvitationRequest {
  email: string;
  displayName: string;
  sendInvitationMessage: boolean;
  inviteRedirectUrl: string;
}

interface ADInvitationResponse {
  success: boolean;
  message?: string;
  data?: {
    invitedUserEmailAddress: string;
    inviteRedeemUrl: string;
    status: string;
    invitationId: string;
    invitedUserDisplayName: string;
  };
}

interface ADInvitationStatusResponse {
  success: boolean;
  data?: {
    userEmail: string;
    userName: string;
    invitationSent: boolean;
    status: 'not_sent' | 'PendingAcceptance' | 'Accepted' | 'expired';
    sentAt?: string;
    acceptedAt?: string;
    invitationId?: string;
    inviteRedeemUrl?: string;
  };
  error?: string;
}

export class FocalPointsApiService {
  constructor(private getAccessToken: () => Promise<string>) {}

  // UPDATED: Send Azure AD B2B Guest Invitation - REAL API CALL
  async sendADInvitation(request: ADInvitationRequest): Promise<{ 
    success: boolean; 
    message?: string; 
    data?: any;
  }> {
    try {
      console.log('📧 Sending Azure AD invitation tooo:', request.email);
      
      const accessToken = await this.getAccessToken();
      
      const response = await fetch(`${API_BASE_URL}/sendadinvitation`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(request)
      });

      console.log('📧 AD Invitation response statusss:', response.status);

      if (response.ok) {
        const data: ADInvitationResponse = await response.json();
        console.log('✅ AD Invitation response:', data);
        
        if (data.success) {
          return {
            success: true,
            message: data.message || 'Azure AD guest invitation sent successfully',
            data: data.data
          };
        } else {
          return {
            success: false,
            message: data.message || 'Failed to send invitation'
          };
        }
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('❌ AD Invitation error:', response.status, errorData);
        
        let userMessage = 'Failed to send Azure AD invitation';
        
        if (response.status === 400) {
          userMessage = 'Invalid invitation data. Please check the email address.';
        } else if (response.status === 403) {
          userMessage = 'Permission denied. Azure AD app may not have correct permissions.';
        } else if (response.status === 500) {
          userMessage = errorData.message || 'Server error while sending invitation.';
        }
        
        return {
          success: false,
          message: userMessage
        };
      }
    } catch (error) {
      console.error('💥 Error sending AD invitation:', error);
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Failed to send AD invitation' 
      };
    }
  }

  // UPDATED: Check AD invitation status - REAL API CALL
  async checkADInvitationStatus(userEmail: string): Promise<{ 
    success: boolean; 
    status?: 'accepted' | 'sent' | 'pending' | 'expired' | 'not_sent';
    message?: string; 
    data?: any;
  }> {
    try {
      console.log('🔍 Checking AD invitation status for:', userEmail);
      
      const accessToken = await this.getAccessToken();
      
      const response = await fetch(
        `${API_BASE_URL}/checkADInvitationStatus?email=${encodeURIComponent(userEmail)}`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': 'application/json'
          }
        }
      );

      console.log('🔍 Status check response:', response.status);

      if (response.ok) {
        const data: ADInvitationStatusResponse = await response.json();
        console.log('✅ Status check data:', data);
        
        if (data.success && data.data) {
          // Map backend status to frontend status
          const statusMap: { [key: string]: 'accepted' | 'sent' | 'pending' | 'expired' | 'not_sent' } = {
            'not_sent': 'not_sent',
            'PendingAcceptance': 'sent',
            'Accepted': 'accepted',
            'expired': 'expired'
          };
          
          return {
            success: true,
            status: statusMap[data.data.status] || 'not_sent',
            data: data.data
          };
        } else {
          return {
            success: false,
            message: 'Failed to check status'
          };
        }
      } else {
        const errorText = await response.text();
        console.error('❌ Status check error:', response.status, errorText);
        
        return {
          success: false,
          message: `Failed to check invitation status (${response.status})`
        };
      }
    } catch (error) {
      console.error('💥 Error checking AD invitation status:', error);
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Failed to check status' 
      };
    }
  }

  // NEW: Sync invitation status from Microsoft Graph API
  async syncADInvitationStatus(userEmail: string): Promise<{ 
    success: boolean; 
    message?: string; 
    data?: any;
  }> {
    try {
      console.log('🔄 Syncing AD invitation status for:', userEmail);
      
      const accessToken = await this.getAccessToken();
      
      const response = await fetch(`${API_BASE_URL}/syncADInvitationStatus`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email: userEmail })
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Status synced:', data);
        
        return {
          success: true,
          message: data.message || 'Status synced successfully',
          data: data.data
        };
      } else {
        const errorText = await response.text();
        console.error('❌ Sync error:', response.status, errorText);
        
        return {
          success: false,
          message: `Failed to sync status (${response.status})`
        };
      }
    } catch (error) {
      console.error('💥 Error syncing status:', error);
      return { 
        success: false, 
        message: error instanceof Error ? error.message : 'Failed to sync status' 
      };
    }
  }

  // Check user authorization
  async checkUserAuthorization(): Promise<{ 
    success: boolean; 
    authorized: boolean; 
    message?: string; 
    user?: any; 
    error?: string 
  }> {
    try {
      console.log('🔐 Checking user authorization...');
      const accessToken = await this.getAccessToken();
      
      const response = await fetch(`${API_BASE_URL}/admin/authorize`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        console.log('✅ Authorization check passed:', data);
        return {
          success: true,
          authorized: data.authorized || false,
          message: data.message,
          user: data.user
        };
      } else {
        const errorText = await response.text();
        console.error('❌ Authorization check failed:', response.status, errorText);
        return { 
          success: false, 
          authorized: false,
          error: `Authorization check failed (${response.status})`,
          message: errorText
        };
      }
    } catch (error) {
      console.error('💥 Error checking authorization:', error);
      return { 
        success: false, 
        authorized: false,
        error: error instanceof Error ? error.message : 'Failed to check authorization' 
      };
    }
  }

  // Get all users
  async getUsers(userType?: UserType, companyId?: number | null): Promise<User[]> {
    const accessToken = await this.getAccessToken();
    
    let url = `${API_BASE_URL}/users`;
    const params = new URLSearchParams();
    
    if (userType) {
      params.append('userType', userType);
    }
    
    if (companyId !== undefined) {
      params.append('companyId', companyId === null ? 'null' : companyId.toString());
    }
    
    if (params.toString()) {
      url += `?${params.toString()}`;
    }
    
    const response = await fetch(url, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch users: ${errorText}`);
    }
    
    const data = await response.json();
    return data.data || [];
  }

  // Helper method: Load users with result object
  async loadUsers(companyId?: number): Promise<{ success: boolean; message?: string; data?: User[] }> {
    try {
      const users = await this.getUsers(undefined, companyId);
      return {
        success: true,
        data: users,
        message: users.length > 0 ? `Loaded ${users.length} users` : undefined
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to load users'
      };
    }
  }

  // Helper method: Load companies with result object
  async loadCompanies(): Promise<{ success: boolean; message?: string; data?: Company[] }> {
    try {
      const companies = await this.getCompanies();
      return {
        success: true,
        data: companies,
        message: companies.length > 0 ? `Loaded ${companies.length} companies` : undefined
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to load companies'
      };
    }
  }

  // Helper method: Load functions for a company with result object
  async loadFunctionsForCompany(companyId: number): Promise<{ success: boolean; message?: string; data?: Function[] }> {
    try {
      const functions = await this.getFunctions(companyId);
      return {
        success: true,
        data: functions
      };
    } catch (error) {
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to load functions'
      };
    }
  }

  // Get all companies
  async getCompanies(): Promise<Company[]> {
    const accessToken = await this.getAccessToken();
    
    const response = await fetch(`${API_BASE_URL}/companies`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch companies: ${errorText}`);
    }
    
    const data = await response.json();
    return data.data || [];
  }

  // Get functions for a company
  async getFunctions(companyId: number): Promise<Function[]> {
    const accessToken = await this.getAccessToken();
    
    const response = await fetch(`${API_BASE_URL}/companies/${companyId}/functions`, {
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`Failed to fetch functions: ${errorText}`);
    }
    
    const data = await response.json();
    return data.data || [];
  }

  // Create user
  async createUser(userData: UserFormData): Promise<{ success: boolean; message?: string; data?: User }> {
    const accessToken = await this.getAccessToken();
    
    // Convert roles array to comma-separated string for backend
    const payload = {
      ...userData,
      role: rolesToString(userData.roles),
    };
    // Remove the frontend-only 'roles' field
    delete (payload as any).roles;

    const response = await fetch(`${API_BASE_URL}/usercreate`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      return {
        success: false,
        message: data.message || data.error || 'Failed to create user'
      };
    }
    
    return {
      success: true,
      message: data.message,
      data: data.data
    };
  }

  // Update user
  async updateUser(userId: number, userData: Partial<UserFormData>): Promise<{ success: boolean; message?: string; data?: User }> {
    const accessToken = await this.getAccessToken();
    
    // Convert roles array to comma-separated string for backend
    const payload: any = { ...userData };
    if (payload.roles) {
      payload.role = rolesToString(payload.roles);
      delete payload.roles;
    }

    const response = await fetch(`${API_BASE_URL}/userupdate/${userId}`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      return {
        success: false,
        message: data.message || data.error || 'Failed to update user'
      };
    }
    
    return {
      success: true,
      message: data.message,
      data: data.data
    };
  }

  // Remove AD Guest User from Entra ID
  async removeADGuestUser(email: string, adUserId?: string | null): Promise<{
    success: boolean;
    message?: string;
    data?: {
      adUserRemoved: boolean;
      reason?: string;
      userType?: string;
      removedUserId?: string;
      removedUserDisplayName?: string;
    };
  }> {
    try {
      console.log('🗑️ Removing AD guest user:', email);
      
      const accessToken = await this.getAccessToken();
      
      const response = await fetch(`${API_BASE_URL}/removeADGuestUser`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          email: email,
          adUserId: adUserId || null
        })
      });

      console.log('🗑️ AD Guest removal response status:', response.status);

      if (response.ok) {
        const data = await response.json();
        console.log('✅ AD Guest removal response:', data);

        if (data.success) {
          return {
            success: true,
            data: data.data,
            message: data.message
          };
        } else {
          return {
            success: false,
            message: data.message || 'Failed to remove guest user from Entra ID'
          };
        }
      } else {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        console.error('❌ AD Guest removal error:', response.status, errorData);

        let userMessage = 'Failed to remove guest user from Entra ID';

        if (response.status === 403) {
          userMessage = 'Permission denied. Azure AD app may not have User.ReadWrite.All permission.';
        } else if (response.status === 500) {
          userMessage = errorData.message || 'Server error while removing guest user.';
        }

        return {
          success: false,
          message: userMessage
        };
      }
    } catch (error) {
      console.error('💥 Error removing AD guest user:', error);
      return {
        success: false,
        message: error instanceof Error ? error.message : 'Failed to remove guest user from Entra ID'
      };
    }
  }

  // Delete user
  async deleteUser(user: User): Promise<{ success: boolean; message?: string }> {
    const accessToken = await this.getAccessToken();
    
    const response = await fetch(`${API_BASE_URL}/userdelete/${user.userId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      }
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      return {
        success: false,
        message: data.message || data.error || 'Failed to delete user'
      };
    }
    
    return {
      success: true,
      message: data.message || `User "${user.userName}" deleted successfully`
    };
  }

  // Toggle blacklist status
  async toggleBlacklist(userId: number, isBlacklisted: boolean): Promise<{ success: boolean; message?: string }> {
    const accessToken = await this.getAccessToken();
    
    const response = await fetch(`${API_BASE_URL}/user/${userId}/blacklist`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ isBlacklisted })
    });
    
    const data = await response.json();
    
    if (!response.ok) {
      return {
        success: false,
        message: data.message || data.error || 'Failed to update blacklist status'
      };
    }
    
    return {
      success: true,
      message: data.message
    };
  }
}